// Phase 2 — HTTP client for the new video draft preview-then-commit flow.
//
// All endpoints sit under /ads/videos/draft and are protected by the same
// owner-id header pattern as the rest of /ads/videos.
//
// Always wraps the underlying axios call with handleApi() per the frontend
// CLAUDE.md rule.

import { handleApi } from "@/utils/handleApi";
import { axiosInstanceWebsite } from "../../instance";
import type {
  AvatarPreviewRequest,
  AvatarPreviewResponse,
  AvatarRecommendationItem,
  AvatarRecommendationsRequest,
  AvatarRecommendationsResponse,
  CreateVideoEditRequest,
  CreateVideoDraftRequest,
  CuratedVoice,
  CuratedVoicePreviewResponse,
  EditDraftScriptRequest,
  GenerateDraftAudioRequest,
  PreviewCuratedVoiceRequest,
  RegenerateDraftImageRequest,
  RegenerateDraftStyleRequest,
  ReviseVideoEditRequest,
  SaveAvatarRequest,
  SavedAvatar,
  SetDraftAudioModeRequest,
  SetDraftVoiceConfigRequest,
  VideoDraftRenderPayloadPreview,
  VideoProductInput,
  VideoProductContextResponse,
  VideoDraftResponse,
  VideoEditContextResponse,
  VideoEditJobResponse,
  VideoEditSessionResponse,
} from "./videoDraft.types";

const BASE = "/ads/videos/draft";
const VOICES_BASE = "/ads/videos/voices";
const VIDEO_CONTEXT_BASE = "/ads/videos/product-context";
const AVATAR_RECOMMENDATIONS_BASE = "/ads/videos/avatar/recommendations";
const VIDEO_EDIT_BASE = "/ads/videos";

export async function getVideoProductContext(productId: string, websiteId?: string | null) {
  if (typeof productId !== "string") {
    throw new Error("productId must be a string");
  }

  return handleApi<VideoProductContextResponse>(
    axiosInstanceWebsite
      .get<VideoProductContextResponse>(VIDEO_CONTEXT_BASE, {
        timeout: 180000,
        params: {
          product_id: productId,
          ...(websiteId ? { website_id: websiteId } : {}),
        },
      })
      .then((r) => r.data),
    false
  );
}

export async function getVideoProductInputContext(productInput: VideoProductInput) {
  const productId = productInput.product_id?.trim();
  if (productId) {
    return getVideoProductContext(productId, productInput.website_id);
  }

  return handleApi<VideoProductContextResponse>(
    axiosInstanceWebsite
      .post<VideoProductContextResponse>(VIDEO_CONTEXT_BASE, {
        product_input: productInput,
      }, { timeout: 180000 })
      .then((r) => r.data),
    false
  );
}

export async function getAvatarRecommendations(payload: AvatarRecommendationsRequest) {
  return handleApi<AvatarRecommendationsResponse>(
    axiosInstanceWebsite
      .post<AvatarRecommendationsResponse>(AVATAR_RECOMMENDATIONS_BASE, payload, { timeout: 600000 })
      .then((r) => r.data),
    false
  );
}

export async function getAvatarRecommendationRole(role: string, payload: AvatarRecommendationsRequest) {
  return handleApi<AvatarRecommendationItem>(
    axiosInstanceWebsite
      .post<AvatarRecommendationItem>(`${AVATAR_RECOMMENDATIONS_BASE}/${role}`, payload, { timeout: 600000 })
      .then((r) => r.data),
    false
  );
}

/**
 * Creates a new draft. The server validates and charges credits, then
 * dispatches the Director Creative call to conversation-engine in background.
 *
 * Returns a VideoDraftResponse with status="directing" — the frontend should
 * then poll getVideoDraft() until status="ready_for_review".
 */
export async function createVideoDraft(payload: CreateVideoDraftRequest) {
  return handleApi<VideoDraftResponse>(
    axiosInstanceWebsite
      .post<VideoDraftResponse>(BASE, payload)
      .then((r) => r.data)
  );
}

/**
 * Polling endpoint. Returns the current state of the draft. The frontend
 * polls this every 5s while in "directing" or "media_generating" states.
 *
 * Pass `showToast=false` because polling errors should not spam the user.
 */
export async function getVideoDraft(referenceId: string) {
  return handleApi<VideoDraftResponse>(
    axiosInstanceWebsite
      .get<VideoDraftResponse>(`${BASE}/${referenceId}`)
      .then((r) => r.data),
    false
  );
}

export async function getVideoDraftRenderPayloadPreview(referenceId: string) {
  return handleApi<VideoDraftRenderPayloadPreview>(
    axiosInstanceWebsite
      .get<VideoDraftRenderPayloadPreview>(`${BASE}/${referenceId}/render-payload-preview`)
      .then((r) => r.data),
    false
  );
}

export async function getVideoEditContext(referenceId: string) {
  return handleApi<VideoEditContextResponse>(
    axiosInstanceWebsite
      .get<VideoEditContextResponse>(`${VIDEO_EDIT_BASE}/${referenceId}/edit-context`)
      .then((r) => r.data),
    false
  );
}

export async function getOrCreateVideoEditSession(referenceId: string) {
  return handleApi<VideoEditSessionResponse>(
    axiosInstanceWebsite
      .post<VideoEditSessionResponse>(`${VIDEO_EDIT_BASE}/${referenceId}/edit-sessions`)
      .then((r) => r.data),
    false
  );
}

export async function getVideoEditSession(sessionId: string) {
  return handleApi<VideoEditSessionResponse>(
    axiosInstanceWebsite
      .get<VideoEditSessionResponse>(`${VIDEO_EDIT_BASE}/edit-sessions/${sessionId}`)
      .then((r) => r.data),
    false
  );
}

export async function createVideoEditFragment(sessionId: string, payload: CreateVideoEditRequest) {
  return handleApi<VideoEditJobResponse>(
    axiosInstanceWebsite
      .post<VideoEditJobResponse>(`${VIDEO_EDIT_BASE}/edit-sessions/${sessionId}/fragments`, payload)
      .then((r) => r.data)
  );
}

export async function createVideoEdit(referenceId: string, payload: CreateVideoEditRequest) {
  return handleApi<VideoEditJobResponse>(
    axiosInstanceWebsite
      .post<VideoEditJobResponse>(`${VIDEO_EDIT_BASE}/${referenceId}/edits`, payload)
      .then((r) => r.data)
  );
}

export async function getVideoEdit(editId: string) {
  return handleApi<VideoEditJobResponse>(
    axiosInstanceWebsite
      .get<VideoEditJobResponse>(`${VIDEO_EDIT_BASE}/edits/${editId}`)
      .then((r) => r.data),
    false
  );
}

export async function reviseVideoEdit(editId: string, payload: ReviseVideoEditRequest) {
  return handleApi<VideoEditJobResponse>(
    axiosInstanceWebsite
      .post<VideoEditJobResponse>(`${VIDEO_EDIT_BASE}/edits/${editId}/revise`, payload)
      .then((r) => r.data)
  );
}

export async function approveVideoEditStartFrame(editId: string, approvedStartFrameUrl?: string | null) {
  return handleApi<VideoEditJobResponse>(
    axiosInstanceWebsite
      .post<VideoEditJobResponse>(`${VIDEO_EDIT_BASE}/edits/${editId}/approve-start-frame`, {
        approved_start_frame_url: approvedStartFrameUrl,
      })
      .then((r) => r.data)
  );
}

export async function renderVideoEdit(editId: string) {
  return handleApi<VideoEditJobResponse>(
    axiosInstanceWebsite
      .post<VideoEditJobResponse>(`${VIDEO_EDIT_BASE}/edits/${editId}/render`)
      .then((r) => r.data)
  );
}

export async function cancelVideoEditFragment(editId: string) {
  return handleApi<VideoEditJobResponse>(
    axiosInstanceWebsite
      .post<VideoEditJobResponse>(`${VIDEO_EDIT_BASE}/edits/${editId}/cancel`)
      .then((r) => r.data)
  );
}

export async function saveVideoEditSession(sessionId: string, mode: "replace" | "copy") {
  return handleApi<VideoEditSessionResponse>(
    axiosInstanceWebsite
      .post<VideoEditSessionResponse>(`${VIDEO_EDIT_BASE}/edit-sessions/${sessionId}/save`, { mode })
      .then((r) => r.data)
  );
}

export async function discardVideoEditSession(sessionId: string) {
  return handleApi<VideoEditSessionResponse>(
    axiosInstanceWebsite
      .post<VideoEditSessionResponse>(`${VIDEO_EDIT_BASE}/edit-sessions/${sessionId}/discard`)
      .then((r) => r.data)
  );
}

/**
 * The user clicked "Aprobar y generar video". Idempotent on the server side.
 * No new credit charge — credits were already charged on createVideoDraft.
 */
export async function approveVideoDraft(referenceId: string) {
  return handleApi<VideoDraftResponse>(
    axiosInstanceWebsite
      .post<VideoDraftResponse>(`${BASE}/${referenceId}/approve`)
      .then((r) => r.data)
  );
}

/**
 * Legacy explicit reject endpoint. In the current preview UX, closing the
 * modal only clears local draft state because credits are charged on approve.
 * Keep this client for older paths / defensive recovery.
 */
export async function rejectVideoDraft(referenceId: string) {
  return handleApi<VideoDraftResponse>(
    axiosInstanceWebsite
      .post<VideoDraftResponse>(`${BASE}/${referenceId}/reject`)
      .then((r) => r.data)
  );
}

/**
 * Phase 5.4: edita el script del draft inline. No regenera imagen, no cuenta
 * como regeneración. Server valida word limits y que script_part_b contenga
 * el productName.
 */
export async function editVideoDraftScript(
  referenceId: string,
  payload: EditDraftScriptRequest
) {
  return handleApi<VideoDraftResponse>(
    axiosInstanceWebsite
      .post<VideoDraftResponse>(`${BASE}/${referenceId}/edit-script`, payload)
      .then((r) => r.data)
  );
}

/**
 * Phase 5.4: regenera la imagen base.
 *
 * - Si se llama sin payload o con instruction null/vacío → regen idéntica
 *   a antes: mismo concept_visual_brief, mismo wrapper Pixar character.
 * - Si se pasa `instruction`, el backend la appendea al brief como capa
 *   extra de personalización (sin perder el wrapper). NO reemplaza nada.
 *
 * Cuenta como 1 regeneración independientemente de si hay instruction.
 */
export async function regenerateVideoDraftImage(
  referenceId: string,
  payload: RegenerateDraftImageRequest = {}
) {
  return handleApi<VideoDraftResponse>(
    axiosInstanceWebsite
      .post<VideoDraftResponse>(`${BASE}/${referenceId}/regenerate-image`, payload)
      .then((r) => r.data)
  );
}

/**
 * Phase 2 V4b (Apr 20 2026): swap the draft's audio_mode between
 * "professional" (ElevenLabs / Gemini TTS from the curated library) and
 * "own" (user-uploaded MP3/WAV). Only valid while the draft is in
 * READY_FOR_REVIEW. "fast" cannot be reached via this endpoint because
 * the Kling inline-audio path must be chosen at draft creation time.
 *
 * When switching TO "own", the frontend MUST first upload the file to
 * S3 (via s3Service) and pass the resulting public URL as
 * `uploaded_audio_url`. The backend persists it into voice_config.
 *
 * Returns the updated draft response so the preview can re-render with
 * the new audio state (OwnVoicePanel for "own", VoiceLibrary for
 * "professional").
 */
export async function setVideoDraftAudioMode(
  referenceId: string,
  payload: SetDraftAudioModeRequest
) {
  return handleApi<VideoDraftResponse>(
    axiosInstanceWebsite
      .post<VideoDraftResponse>(`${BASE}/${referenceId}/set-audio-mode`, payload)
      .then((r) => r.data)
  );
}

export async function setVideoDraftVoiceConfig(
  referenceId: string,
  payload: SetDraftVoiceConfigRequest
) {
  return handleApi<VideoDraftResponse>(
    axiosInstanceWebsite
      .post<VideoDraftResponse>(`${BASE}/${referenceId}/voice-config`, payload)
      .then((r) => r.data)
  );
}

/**
 * Phase 5.4: re-corre el director Gemini con (opcionalmente) un pattern
 * forzado. Vuelve el draft a directing — el frontend debe resumir polling
 * hasta que vuelva a ready_for_review con el nuevo payload + imagen.
 */
export async function regenerateVideoDraftStyle(
  referenceId: string,
  payload: RegenerateDraftStyleRequest = {}
) {
  return handleApi<VideoDraftResponse>(
    axiosInstanceWebsite
      .post<VideoDraftResponse>(`${BASE}/${referenceId}/regenerate-style`, payload)
      .then((r) => r.data)
  );
}

/**
 * Phase 5.5: lista todos los drafts del owner que están en estados
 * intermedios (no terminales). Usado por la galería de videos para
 * mostrar "loading cards" de los drafts que están renderizando en
 * background, sobreviviendo refresh y navegación a otras partes de
 * Fluxi. Backend retorna drafts recuperables: READY_FOR_REVIEW y los
 * estados post-approve GENERATING_IMAGE, WAITING_SCENES, MERGING.
 * Lista vacía si no hay nada en vuelo. NO toastea errores porque se
 * llama on-mount + polling continuo.
 */
export async function listInFlightVideoDrafts() {
  return handleApi<VideoDraftResponse[]>(
    axiosInstanceWebsite
      .get<VideoDraftResponse[]>(`${BASE}/in-flight`)
      .then((r) => r.data),
    false
  );
}

/**
 * Curated Gemini voice catalog for modeling voice-over. Static voice
 * metadata and compressed preview samples are cached for the session.
 */
export async function listCuratedVoices() {
  return handleApi<CuratedVoice[]>(
    axiosInstanceWebsite
      .get<CuratedVoice[]>(VOICES_BASE)
      .then((r) => r.data),
    false
  );
}

export async function previewCuratedVoice(
  voiceId: string,
  payload: PreviewCuratedVoiceRequest
) {
  return handleApi<CuratedVoicePreviewResponse>(
    axiosInstanceWebsite
      .post<CuratedVoicePreviewResponse>(
        `${VOICES_BASE}/preview/${encodeURIComponent(voiceId)}`,
        payload,
        { timeout: 120000 }
      )
      .then((r) => r.data),
    false
  );
}

/**
 * Phase 2 V2 — Triggers deferred TTS generation over the current draft
 * script with the voice the user picked. Can be called repeatedly to
 * audition different voices; each call overwrites draft.audio_url +
 * draft.voice_config. Does NOT count as a regeneration (voice swaps are
 * free by design).
 */
export async function generateVideoDraftAudio(
  referenceId: string,
  payload: GenerateDraftAudioRequest
) {
  return handleApi<VideoDraftResponse>(
    axiosInstanceWebsite
      .post<VideoDraftResponse>(`${BASE}/${referenceId}/generate-audio`, payload)
      .then((r) => r.data)
  );
}

/**
 * Phase 2 V2 — Live avatar portrait preview for the Avatar Wizard.
 * Called on every avatar_config change (debounced on the client) so the
 * user can audition their presenter before committing to a draft. No
 * credits charged. Doesn't toast on error — the wizard surfaces errors
 * inline near the preview.
 */
export async function previewAvatar(payload: AvatarPreviewRequest) {
  return handleApi<AvatarPreviewResponse>(
    axiosInstanceWebsite
      .post<AvatarPreviewResponse>("/ads/videos/avatar/preview", payload, { timeout: 300000 })
      .then((r) => r.data),
    false
  );
}

/**
 * Phase 2 V2 — Fase C. "Mis avatares" CRUD.
 *
 * listMyAvatars: used by the wizard to render the "Mis avatares guardados"
 *   section. No toast on error — the wizard degrades to an empty section.
 * saveMyAvatar: called from the custom builder ("Guardar en mi elenco") and
 *   from the preset commit path (with source="preset").
 * deleteMyAvatar: soft delete (backend sets deleted_at). Idempotent.
 */
const MINE_BASE = "/ads/videos/avatar/mine";

export async function listMyAvatars() {
  return handleApi<SavedAvatar[]>(
    axiosInstanceWebsite
      .get<SavedAvatar[]>(MINE_BASE)
      .then((r) => r.data),
    false
  );
}

export async function saveMyAvatar(payload: SaveAvatarRequest) {
  return handleApi<SavedAvatar>(
    axiosInstanceWebsite
      .post<SavedAvatar>(MINE_BASE, payload)
      .then((r) => r.data)
  );
}

export async function deleteMyAvatar(id: number) {
  return handleApi<{ deleted: boolean; id: number }>(
    axiosInstanceWebsite
      .delete<{ deleted: boolean; id: number }>(`${MINE_BASE}/${id}`)
      .then((r) => r.data)
  );
}
